import React, { useState, useEffect } from 'react';
import { 
  Button, 
  Card, 
  CardBody, 
  CardHeader, 
  Col, 
  Container, 
  Modal, 
  ModalBody, 
  ModalFooter, 
  ModalHeader, 
  Row,
  Input,
  Label,
  FormFeedback,
  Form
} from 'reactstrap';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import { Link } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DeleteModal from '../../../Components/Common/DeleteModal';

// Redux
import { useSelector, useDispatch } from 'react-redux';
import { getAttributes, addNewAttribute, updateAttribute, deleteAttribute } from '../../../slices/thunks';
import { getUserRole } from '../../../helpers/role_helper';
import { getLoggedinUser } from '../../../api';

const ProductAttribute = () => {
  const dispatch = useDispatch();
  const [modal_list, setModal_list] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState(null);

  // Get user info for filtering
  const userProfile = getLoggedinUser();
  const userRole = getUserRole(userProfile);
  const userId = userProfile?.id || userProfile?.uid || userProfile?._id;

  // Get attributes from Redux state
  const attributesRaw = useSelector((state) => state.Ecommerce?.attributes);
  const attributes = Array.isArray(attributesRaw) ? attributesRaw : [];

  // Field type options
  const fieldTypes = [
    { label: 'Text', value: 'Text' },
    { label: 'Number', value: 'Number' },
    { label: 'Email', value: 'Email' },
    { label: 'Password', value: 'Password' },
    { label: 'Textarea', value: 'Textarea' },
    { label: 'Select', value: 'Select' },
    { label: 'Checkbox', value: 'Checkbox' },
    { label: 'Radio', value: 'Radio' },
  ];

  // Status options
  const statusOptions = [
    { label: 'Active', value: 'Active' },
    { label: 'Inactive', value: 'Inactive' },
  ];

  // Fetch attributes on mount
  useEffect(() => {
    const params = {};
    if (userRole === "seller" && userId) {
      params.seller_id = userId;
    }
    dispatch(getAttributes(params));
  }, [dispatch, userRole, userId]);

  const tog_list = (attribute = null) => {
    if (modal_list) {
      setModal_list(false);
      setEditingAttribute(null);
      validation.resetForm();
    } else {
      setModal_list(true);
      if (attribute) {
        setEditingAttribute(attribute);
        validation.setValues({
          name: attribute.name || '',
          field_type: attribute.field_type || 'Text',
          status: attribute.status || 'Active',
        });
      } else {
        setEditingAttribute(null);
      }
    }
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: '',
      field_type: 'Text',
      status: 'Active',
    },
    validationSchema: Yup.object({
      name: Yup.string().required('Attribute name is required'),
      field_type: Yup.string().required('Field type is required'),
      status: Yup.string().required('Status is required'),
    }),
    onSubmit: async (values) => {
      try {
        if (editingAttribute) {
          // Update attribute
          const updateData = {
            id: editingAttribute.id || editingAttribute._id,
            name: values.name,
            field_type: values.field_type,
            status: values.status,
          };
          await dispatch(updateAttribute(updateData)).unwrap();
        } else {
          // Add new attribute
          const newAttribute = {
            name: values.name,
            field_type: values.field_type,
            status: values.status,
            seller_id: userRole === "seller" ? userId : null,
            created_by: userId,
          };
          await dispatch(addNewAttribute(newAttribute)).unwrap();
        }
        tog_list(); // Close modal on success
        validation.resetForm();
      } catch (error) {
        // Error toast is handled by thunk
        console.error("Form submission error:", error);
      }
    },
  });

  const handleDelete = async (attributeId) => {
    try {
      await dispatch(deleteAttribute(attributeId)).unwrap();
      setDeleteModal(false);
    } catch (error) {
      // Error toast is handled by thunk
      console.error("Delete error:", error);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };

  document.title = "Product Attribute - Topcoinland";

  return (
    <React.Fragment>
      <div className="page-content">
        <ToastContainer position="top-right" autoClose={3000} />
        <Container fluid>
          <BreadCrumb title="Product Attribute" pageTitle="Product" />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">Product Attribute</h4>
                </CardHeader>

                <CardBody>
                  <div className="listjs-table" id="customerList">
                    <Row className="g-4 mb-3">
                      <Col className="col-sm-auto">
                        <div>
                          <Button 
                            color="success" 
                            className="add-btn me-1" 
                            onClick={() => tog_list()} 
                            id="create-btn"
                          >
                            <i className="ri-add-line align-bottom me-1"></i> Add
                          </Button>
                        </div>
                      </Col>
                      <Col className="col-sm">
                        <div className="d-flex justify-content-sm-end">
                          <div className="search-box ms-2">
                            <input type="text" className="form-control search" placeholder="Search..." />
                            <i className="ri-search-line search-icon"></i>
                          </div>
                        </div>
                      </Col>
                    </Row>

                    <div className="table-responsive table-card mt-3 mb-1">
                      <table className="table align-middle table-nowrap" id="customerTable">
                        <thead className="table-light">
                          <tr>
                            <th scope="col" style={{ width: "50px" }}>
                              <div className="form-check">
                                <input className="form-check-input" type="checkbox" id="checkAll" value="option" />
                              </div>
                            </th>
                            <th className="sort" data-sort="customer_name">Name</th>
                            <th className="sort" data-sort="slug">Slug</th>
                            <th className="sort" data-sort="date">Field Type</th>                           
                            <th className="sort" data-sort="status">Created At</th>
                            <th className="sort" data-sort="status">Status</th>
                            <th className="sort" data-sort="action">Operations</th>
                          </tr>
                        </thead>
                        <tbody className="list form-check-all">
                          {attributes.length > 0 ? attributes.map((item, index) => (
                            <tr key={index}>
                              {/* Checkbox */}
                              <th scope="row">
                                <div className="form-check">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    name="checkAll"
                                    value={index + 1}
                                  />
                                </div>
                              </th>

                              {/* Name */}
                              <td>{item.name}</td>

                              {/* Slug */}
                              <td>{item.slug || '-'}</td>

                              {/* Field Type */}
                              <td>{item.field_type || 'Text'}</td>

                              {/* Created At */}
                              <td>{formatDate(item.created_at)}</td>

                              {/* Status */}
                              <td className="status">
                                <span
                                  className={`badge ${
                                    item.status === "Active"
                                      ? "bg-success-subtle text-success"
                                      : "bg-danger-subtle text-danger"
                                  } text-uppercase`}
                                >
                                  {item.status}
                                </span>
                              </td>

                              {/* Operations */}
                              <td>
                                <div className="d-flex gap-2">
                                  <div className="edit">
                                    <button
                                      className="btn btn-sm btn-success edit-item-btn"
                                      onClick={() => tog_list(item)}
                                    >
                                      <i className="ri-edit-line"></i>
                                    </button>
                                  </div>

                                  <div className="remove">
                                    <button
                                      className="btn btn-sm btn-danger remove-item-btn"
                                      onClick={() => {
                                        setDeleteModal(true);
                                        setEditingAttribute(item);
                                      }}
                                    >
                                      <i className="ri-delete-bin-2-line"></i>
                                    </button>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          )) : (
                            <tr>
                              <td colSpan="7" className="text-center py-5">
                                <p className="text-muted">No attributes found. Click "Add" to create one.</p>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                      <div className="noresult" style={{ display: "none" }}>
                        <div className="text-center">
                          <lord-icon src="https://cdn.lordicon.com/msoeawqm.json" trigger="loop"
                            colors="primary:#121331,secondary:#08a88a" style={{ width: "75px", height: "75px" }}>
                          </lord-icon>
                          <h5 className="mt-2">Sorry! No Result Found</h5>
                          <p className="text-muted mb-0">We've searched more than 150+ Attributes We did not find any
                            attributes for you search.</p>
                        </div>
                      </div>
                    </div>

                    <div className="d-flex justify-content-end">
                      <div className="pagination-wrap hstack gap-2">
                        <Link className="page-item pagination-prev disabled" to="#">
                          Previous
                        </Link>
                        <ul className="pagination listjs-pagination mb-0"></ul>
                        <Link className="page-item pagination-next" to="#">
                          Next
                        </Link>
                      </div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Add/Edit Modal */}
          <Modal isOpen={modal_list} toggle={tog_list} centered>
            <ModalHeader toggle={tog_list}>
              {editingAttribute ? 'Edit Product Attribute' : 'Add Product Attribute'}
            </ModalHeader>
            <Form onSubmit={validation.handleSubmit}>
              <ModalBody>
                <Row>
                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="name" className="form-label">
                        Name <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="text"
                        className="form-control"
                        id="name"
                        name="name"
                        placeholder="Enter attribute name (e.g., Color, Size, Weight)"
                        value={validation.values.name}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={validation.touched.name && validation.errors.name ? true : false}
                      />
                      {validation.touched.name && validation.errors.name ? (
                        <FormFeedback type="invalid">{validation.errors.name}</FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="field_type" className="form-label">
                        Field Type <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="select"
                        className="form-select"
                        id="field_type"
                        name="field_type"
                        value={validation.values.field_type}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={validation.touched.field_type && validation.errors.field_type ? true : false}
                      >
                        {fieldTypes.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </Input>
                      {validation.touched.field_type && validation.errors.field_type ? (
                        <FormFeedback type="invalid">{validation.errors.field_type}</FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                  <Col lg={12}>
                    <div className="mb-3">
                      <Label htmlFor="attribute-status" className="form-label">
                        Status <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="select"
                        className="form-select"
                        id="attribute-status"
                        name="status"
                        value={validation.values.status}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                        invalid={validation.touched.status && validation.errors.status ? true : false}
                      >
                        {statusOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </Input>
                      {validation.touched.status && validation.errors.status ? (
                        <FormFeedback type="invalid">{validation.errors.status}</FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                </Row>
              </ModalBody>
              <ModalFooter>
                <Button color="light" onClick={tog_list}>
                  Cancel
                </Button>
                <Button color="primary" type="submit">
                  {editingAttribute ? 'Update' : 'Add'}
                </Button>
              </ModalFooter>
            </Form>
          </Modal>

          {/* Delete Modal */}
          <DeleteModal
            show={deleteModal}
            onDeleteClick={() => {
              if (editingAttribute) {
                handleDelete(editingAttribute.id || editingAttribute._id);
              }
            }}
            onCloseClick={() => {
              setDeleteModal(false);
              setEditingAttribute(null);
            }}
          />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ProductAttribute;
